/**
 * v109.9.73 — Unified "Access" dialog.
 *
 * One pill button, one dialog. The dialog exposes the module → tabs →
 * sub-tabs as an expandable tree. At every level Master Admin can:
 *   • see who currently has access to that specific scope
 *   • toggle "Mark as Common" (visible to every user)
 *   • grant to All Employees or Specific Employees (opens card picker)
 *   • drill into sub-levels ("Give access at Sub level")
 *   • revoke an individual user chip
 *
 * Rule: grants at any level CASCADE downwards. Granting the module also
 * grants every tab and every sub-tab; granting a tab also grants every
 * sub-tab under it. This mirrors Master Admin's own baseline exactly.
 */
import { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck, Save, X, ChevronDown, ChevronRight,
  Users as UsersIcon, Globe2, UserSquare2, Trash2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import UserPickerDialog from "./UserPickerDialog";

const LEGACY_ROLE_OPTIONS = [
  "hr_admin", "sales_admin", "sales_team", "accounts_admin",
  "dispatch_admin", "it_admin", "crm_manager", "documentation_team",
  "trainer", "employee",
];
const TEMP_ROLE_KEYS = ["ex_employee_temp", "candidate_temp"];

export default function ModuleAccessButtons({ moduleKey }) {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [open, setOpen] = useState(false);
  if (!isMaster || !moduleKey) return null;
  return (
    <>
      <div className="flex items-center gap-1.5" data-testid={`ma-quickbar-${moduleKey}`}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-sm shadow-indigo-500/30 transition-all"
          data-testid={`ma-quick-access-${moduleKey}`}
          title="Manage module + tab + sub-tab access for this page"
        >
          <ShieldCheck size={13} /> Access
        </button>
      </div>
      {open && <AccessDialog moduleKey={moduleKey} onClose={() => setOpen(false)} />}
    </>
  );
}

function AccessDialog({ moduleKey, onClose }) {
  const [tree, setTree] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [expanded, setExpanded] = useState({}); // {scope: true}
  const [picker, setPicker] = useState(null); // {scope, label}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, u] = await Promise.all([
        api.get(`/access/module-tree/${moduleKey}`),
        api.get("/employees"),
      ]);
      setTree(t.data);
      setActiveUsers(
        (u.data || []).filter(
          (x) => x.role !== "master_admin" && x.is_active !== false &&
                 !x.is_temp_account && !TEMP_ROLE_KEYS.includes(x.role)
        )
      );
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }, [moduleKey]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (scope) => setExpanded((e) => ({ ...e, [scope]: !e[scope] }));

  const grantScope = async (scope, userIds) => {
    setBusy(true);
    try {
      await api.post("/access/scope/grant", { scope, user_ids: userIds });
      toast.success(
        userIds === "all"
          ? `Granted to all active employees · ${scope}`
          : `Granted to ${userIds.length} employee${userIds.length === 1 ? "" : "s"} · ${scope}`
      );
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleCommon = async (scope, next) => {
    setBusy(true);
    try {
      await api.post("/access/scope/common-toggle", { scope, on: next });
      toast.success(next ? `${scope} · marked Common` : `${scope} · removed from Common`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const revokeUser = async (scope, userId, name) => {
    if (!window.confirm(`Revoke ${name}'s override at "${scope}"?`)) return;
    try {
      await api.delete(`/access/scope/user?scope=${encodeURIComponent(scope)}&user_id=${encodeURIComponent(userId)}`);
      toast.success(`Revoked · ${name}`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // v109.9.74 — Remove ALL access for this module (users + custom roles +
  // legacy roles + every Common scope for the module). When invoked at
  // module level, every tab and every sub-tab is transitively wiped too
  // because the backend $unset removes `overrides.<module_key>` in full.
  const revokeAllForModule = async () => {
    if (!window.confirm(
      `Remove access for ALL users on "${tree?.module?.label || moduleKey}"?\n\n` +
      `This closes access for:\n` +
      `  • Every user's per-user override\n` +
      `  • Every custom role's permission for this module\n` +
      `  • Every legacy role's module list\n` +
      `  • All Common scopes (module, tabs, sub-tabs)\n\n` +
      `Only Master Admin retains access. Continue?`
    )) return;
    setBusy(true);
    try {
      const res = await api.post(`/access/module-grants/${moduleKey}/revoke-all`);
      toast.success(
        `Access removed for all users · ${res.data.roles_stripped} custom role${res.data.roles_stripped === 1 ? "" : "s"}` +
        ` · ${res.data.legacy_stripped} legacy role${res.data.legacy_stripped === 1 ? "" : "s"}`
      );
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={!picker} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ma-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-indigo-600" />
              Access · {tree?.module?.label || moduleKey}
            </DialogTitle>
          </DialogHeader>

          {!tree ? (
            <div className="py-8 text-center text-slate-400 text-sm">Loading access tree…</div>
          ) : (
            <div className="space-y-3">
              <AccessNode
                node={tree.module}
                depth={0}
                expanded={expanded[tree.module.scope]}
                onToggleExpand={() => toggleExpand(tree.module.scope)}
                canDrillDown={tree.tabs.length > 0}
                childLabel={`${tree.tabs.length} tab${tree.tabs.length === 1 ? "" : "s"}`}
                onGrantAll={() => grantScope(tree.module.scope, "all")}
                onGrantSpecific={() => setPicker({ scope: tree.module.scope, label: tree.module.label })}
                onToggleCommon={(v) => toggleCommon(tree.module.scope, v)}
                onRevokeUser={(uid, name) => revokeUser(tree.module.scope, uid, name)}
                onRemoveAll={revokeAllForModule}
                busy={busy}
              >
                {expanded[tree.module.scope] && (
                  <div className="pl-4 border-l-2 border-indigo-200 dark:border-indigo-500/30 mt-2 space-y-2">
                    {tree.tabs.map((tab) => (
                      <AccessNode
                        key={tab.scope}
                        node={tab}
                        depth={1}
                        expanded={expanded[tab.scope]}
                        onToggleExpand={() => toggleExpand(tab.scope)}
                        canDrillDown={(tab.subtabs || []).length > 0}
                        childLabel={`${tab.subtabs.length} sub-tab${tab.subtabs.length === 1 ? "" : "s"}`}
                        onGrantAll={() => grantScope(tab.scope, "all")}
                        onGrantSpecific={() => setPicker({ scope: tab.scope, label: tab.label })}
                        onToggleCommon={(v) => toggleCommon(tab.scope, v)}
                        onRevokeUser={(uid, name) => revokeUser(tab.scope, uid, name)}
                        busy={busy}
                      >
                        {expanded[tab.scope] && tab.subtabs.length > 0 && (
                          <div className="pl-4 border-l-2 border-emerald-200 dark:border-emerald-500/30 mt-2 space-y-2">
                            {tab.subtabs.map((sub) => (
                              <AccessNode
                                key={sub.scope}
                                node={sub}
                                depth={2}
                                expanded={false}
                                onToggleExpand={() => {}}
                                canDrillDown={false}
                                onGrantAll={() => grantScope(sub.scope, "all")}
                                onGrantSpecific={() => setPicker({ scope: sub.scope, label: sub.label })}
                                onToggleCommon={(v) => toggleCommon(sub.scope, v)}
                                onRevokeUser={(uid, name) => revokeUser(sub.scope, uid, name)}
                                busy={busy}
                              />
                            ))}
                          </div>
                        )}
                      </AccessNode>
                    ))}
                  </div>
                )}
              </AccessNode>
            </div>
          )}

          <div className="mt-3 rounded-md bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-2 text-[10.5px] text-slate-600 dark:text-slate-300 leading-snug">
            <b>Cascade rule:</b> access granted at Module → cascades to every Tab and Sub-Tab. Tab grants cascade to Sub-Tabs. Users get <b>View + Edit + Delete</b> at every level under the picked scope, exactly like Master Admin.
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={onClose}><X size={13} className="mr-1.5" /> Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {picker && (
        <UserPickerDialog
          open
          onClose={() => setPicker(null)}
          onDone={async (ids) => {
            setPicker(null);
            if (ids.length === 0) { toast.error("No users picked"); return; }
            await grantScope(picker.scope, ids);
          }}
          users={activeUsers}
          initialSelectedIds={[]}
          title={`Grant "${picker.label}" access to…`}
          roleOptions={LEGACY_ROLE_OPTIONS}
        />
      )}
    </>
  );
}

// A single row in the tree: Module | Tab | Sub-tab. Depth drives styling.
function AccessNode({
  node, depth, expanded, onToggleExpand, canDrillDown, childLabel,
  onGrantAll, onGrantSpecific, onToggleCommon, onRevokeUser, onRemoveAll,
  busy, children,
}) {
  const tone =
    depth === 0 ? { pill: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200 border-indigo-300 dark:border-indigo-500/40", label: "MODULE" }
  : depth === 1 ? { pill: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 border-emerald-300 dark:border-emerald-500/40", label: "TAB" }
  :               { pill: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 border-amber-300 dark:border-amber-500/40",       label: "SUB-TAB" };
  const users = node.users || [];

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3" data-testid={`ma-access-node-${node.scope.replace(/\./g, '_')}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center text-[9.5px] font-extrabold px-1.5 py-0.5 rounded border ${tone.pill} uppercase tracking-wide`}>{tone.label}</span>
            <span className="text-[14px] font-extrabold text-slate-900 dark:text-white">{node.label}</span>
            <span className="text-[10px] mono text-slate-400">{node.scope}</span>
            {node.in_common && (
              <span className="inline-flex items-center gap-0.5 text-[9.5px] font-extrabold px-1.5 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wide" data-keep-color>
                <Globe2 size={9} /> Common
              </span>
            )}
          </div>
        </div>
        {canDrillDown && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10.5px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
            data-testid={`ma-access-drill-${node.scope.replace(/\./g, '_')}`}
          >
            {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
            {expanded ? "Hide" : "Give access at Sub level"} · {childLabel}
          </button>
        )}
      </div>

      {/* Who has access */}
      <div className="rounded-md bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 p-2 mb-2">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1.5">
          <UsersIcon size={10} /> Who has access · {users.length} user{users.length === 1 ? "" : "s"}
        </div>
        {users.length === 0 ? (
          <div className="text-[11px] italic text-slate-500 dark:text-slate-400">
            Nobody yet {node.in_common ? "" : "· grant via the buttons below or expand into sub-levels"}.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {users.map((u) => (
              <span
                key={u.user_id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                title={u.email}
                data-testid={`ma-access-user-${node.scope.replace(/\./g, '_')}-${u.user_id}`}
              >
                <UserSquare2 size={10} className="text-slate-400" />
                {u.name}
                {u.has_override && (
                  <button
                    type="button"
                    onClick={() => onRevokeUser(u.user_id, u.name)}
                    className="ml-0.5 w-4 h-4 grid place-items-center rounded-full hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-500"
                    title="Revoke this user's per-user override"
                    data-testid={`ma-access-revoke-${node.scope.replace(/\./g, '_')}-${u.user_id}`}
                  >
                    <X size={9} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => onToggleCommon(!node.in_common)}
          disabled={busy}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold transition-all ${
            node.in_common
              ? "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-500/30"
              : "bg-white dark:bg-slate-900 border border-emerald-300 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/15"
          }`}
          data-testid={`ma-access-common-${node.scope.replace(/\./g, '_')}`}
        >
          <Globe2 size={11} />
          {node.in_common ? `Common ✓ (click to remove)` : `Mark ${tone.label.toLowerCase()} as Common`}
        </button>
        <button
          type="button"
          onClick={onGrantAll}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95 shadow-sm shadow-indigo-500/30 transition-all"
          data-testid={`ma-access-all-${node.scope.replace(/\./g, '_')}`}
        >
          <UsersIcon size={11} /> All Employees
        </button>
        <button
          type="button"
          onClick={onGrantSpecific}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold bg-white dark:bg-slate-900 border border-indigo-300 dark:border-indigo-500/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/15 transition-all"
          data-testid={`ma-access-specific-${node.scope.replace(/\./g, '_')}`}
        >
          <UserSquare2 size={11} /> Specific Employees
        </button>
        {onRemoveAll && (
          <button
            type="button"
            onClick={onRemoveAll}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold bg-white dark:bg-slate-900 border border-rose-300 dark:border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/15 transition-all"
            data-testid={`ma-access-remove-all-${node.scope.replace(/\./g, '_')}`}
            title="Close module access — removes every user's access to this module, and all its tabs & sub-tabs"
          >
            <RotateCcw size={11} /> Remove All Access
          </button>
        )}
      </div>

      {children}
    </div>
  );
}
